import React, { useContext, useMemo } from 'react';

import { preferredDomainForPath } from 'shared/components/common/link';

type PageDomainOverride = {
  domain: string,
  pageRoute?: string | null,
  userCreated?: boolean,
  pendingVerification?: boolean,
};

export type PageDomainContextType = {
  host: string,
  pathname: string,
  pageDomainOverrides: Map<string, string>,
  overriddenDomains: Set<string>,
  rootDomain: string
};

export const PageDomainContext = React.createContext<PageDomainContextType>({
  host: '',
  pathname: '',
  pageDomainOverrides: new Map<string, string>(),
  overriddenDomains: new Set<string>(),
  rootDomain: ''
});

export type Props = {
  domains: Array<PageDomainOverride>,
  host: string,
  pathname: string
};

const routeGroups: { [key: string]: string[] } = {
  // The /order route is a special case where all order-related routes
  // should be on the same domain
  '/order': ['/checkout', '/confirm', '/account']
};

/**
 * Obtain root domain
 * Prioritize in this order:
 * 1. user created homepage domain
 * 2. toast site domain (*.toast.site)
 * 3. host domain
 */
const getRootDomain = (domains: PageDomainOverride[], host: string): string => {
  const userCreatedHomepageDomain = domains?.find(d =>
    d !== null &&
    d.userCreated === true &&
    d.pendingVerification === false &&
    !d.pageRoute)?.domain;

  const defaultSiteHomepageDomain = domains?.find(d =>
    d !== null &&
    d.userCreated === false &&
    d.domain.endsWith('toast.site'))?.domain;

  const hostRootDomain = host.split('.').reverse()
    .splice(0, 2)
    .reverse()
    .join('.');
  const rootDomain = domains.find(d => d.domain === hostRootDomain) ? hostRootDomain : host;

  return userCreatedHomepageDomain ?? defaultSiteHomepageDomain ?? rootDomain;
};

export const PageDomainProvider = ({ domains, host, pathname, children }: React.PropsWithChildren<Props>) => {
  const rootDomain = useMemo(() => getRootDomain(domains, host), [domains, host]);
  const pageDomainOverrides = new Map<string, string>();
  const overriddenDomains = new Set<string>();
  domains.forEach(d => {
    if(d.domain && d.pageRoute) {
      // If the current path is overridden by a domain, but the current domain does not match the overridding domain,
      // don't use the overridding domain. This allows users to stay on the current domain so that we don't lose any cookies
      // switching to the overridding domain.
      // For example, if a site has the domains 'site.com' and 'order.site.com', and the user is currently at site.com/order, let them
      // complete the order workflow on the root domain.
      if(
        host === d.domain
        || !pathname.startsWith(d.pageRoute) && !routeGroups[d.pageRoute]?.some(page => pathname.startsWith(page))
      ) {
        pageDomainOverrides.set(d.pageRoute, d.domain);
        overriddenDomains.add(d.domain);

        routeGroups[d.pageRoute]?.forEach(page => pageDomainOverrides.set(page, d.domain));
      }
    }
  });


  return (
    <PageDomainContext.Provider
      value={{
        host,
        pathname,
        pageDomainOverrides,
        overriddenDomains,
        rootDomain
      }}>
      {children}
    </PageDomainContext.Provider>
  );
};


export const applyURLDomainOverride = (urlStr: string, pageDomainOverrides: Map<string, string>): string => {
  if(pageDomainOverrides.size === 0) {
    return urlStr;
  }
  let url: URL;
  try {
    url = new URL(urlStr);
  } catch{
    return urlStr;
  }
  const preferredDomain = preferredDomainForPath(url.pathname, pageDomainOverrides);
  if(!preferredDomain) {
    return urlStr;
  }

  // Substitute the overriding domain in the URL.
  url.hostname = preferredDomain;
  return url.toString();
};


export const usePageDomains = () => {
  return useContext(PageDomainContext);
};
